import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query/react';
import { Job, ProblemCode } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { DifficultyMeter, LanguageCode, RelativeTime, TaskTypeBadge } from '../../task/TaskBadges';
import { useListTasksInfiniteQuery } from '../../task/taskEndpoints';
import { JobChunkTable } from '../JobChunkTable';
import { JobCostCard } from '../JobCostCard';
import { JobStatusPill } from '../JobBadges';
import { useCancelJobMutation, useGetJobQuery } from '../jobEndpoints';

/** `text` cut to `max` characters, with an ellipsis when it was cut (QZ-D47). */
const truncate = (text: string, max: number): string =>
  text.length <= max ? text : `${text.slice(0, max)}…`;

/** The progress bar's class for the job's status: animated while running, tinted once it ends. */
const progressBarClass = (status: Job['status']): string => {
  switch (status) {
    case 'running':
      return 'progress-bar progress-bar-striped progress-bar-animated';
    case 'done':
      return 'progress-bar bg-success';
    case 'failed':
      return 'progress-bar bg-danger';
    default:
      return 'progress-bar';
  }
};

/**
 * QZ-D20, QZ-D49, QZ-D55, QZ-D56, QZ-D57: a job's progress page at `/jobs/:jobId`. It polls
 * while the job is active, shows its chunks, latest drafts and cost, and offers cancel while
 * the job is planning or running.
 */
export const JobPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [pollingInterval, setPollingInterval] = useState(5_000);
  const {
    data: job,
    error,
    isLoading,
    refetch: refetchJob,
  } = useGetJobQuery(jobId ?? skipToken, { pollingInterval, skipPollingIfUnfocused: true });

  const loadErrorCode = problemCode(error);
  const notFound =
    !job &&
    !!error &&
    (loadErrorCode === 'JobNotFound' || (error as FetchBaseQueryError).status === 404);

  useEffect(() => {
    if (
      notFound ||
      job?.status === 'done' ||
      job?.status === 'failed' ||
      job?.status === 'cancelled'
    ) {
      setPollingInterval(0);
    } else if (job?.status === 'planning' || job?.status === 'running') {
      setPollingInterval(5_000);
    }
  }, [job?.status, notFound]);

  const isPlanning = job?.status === 'planning';
  const tasksQuery = useListTasksInfiniteQuery(
    jobId && job && job.status !== 'planning' ? { status: 'draft', jobId } : skipToken
  );
  const drafts = tasksQuery.data?.pages[0]?.tasks.slice(0, 5) ?? [];

  // QZ-D59: a poll's new job snapshot may carry freshly drafted tasks; the drafts list has no
  // tag linking it to the job, so it is nudged with an explicit refetch rather than invalidation.
  const prevDrafted = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!job) {
      return;
    }
    const sum = job.chunks.reduce((total, chunk) => total + chunk.drafted, 0);
    const prev = prevDrafted.current;
    if (!isPlanning && !tasksQuery.isUninitialized && prev !== undefined && prev !== sum) {
      tasksQuery.refetch();
    }
    prevDrafted.current = sum;
  }, [job]);

  const [cancelJob, { isLoading: cancelling }] = useCancelJobMutation();
  const [confirming, setConfirming] = useState(false);
  const [cancelError, setCancelError] = useState<ProblemCode | 'unknown' | undefined>(undefined);

  const handleCancel = async () => {
    if (!jobId) {
      return;
    }
    setCancelError(undefined);
    try {
      await cancelJob(jobId).unwrap();
      setConfirming(false);
    } catch (thrown) {
      const code = problemCode(thrown);
      setCancelError(code ?? 'unknown');
      if (code === 'JobEnded') {
        refetchJob();
      }
    }
  };

  const openTask = (taskId: string) => navigate(`/tasks/${taskId}`);

  const finishedPercent =
    job && job.chunks.length > 0
      ? Math.round(
          (100 *
            job.chunks.filter((chunk) => chunk.status === 'done' || chunk.status === 'failed')
              .length) /
            job.chunks.length
        )
      : 0;

  return (
    <main className="qz-main">
      <div className="qz-container">
        <Link className="qz-back" to="/jobs">
          <Icon name="arrow-left" /> {t('job.page.back')}
        </Link>

        {isLoading && <p className="qz-muted">{t('job.page.loading')}</p>}

        {!job && !isLoading && error && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>
              {t(
                notFound
                  ? 'job.page.notFound'
                  : loadErrorCode
                    ? `error.${loadErrorCode}`
                    : 'error.unknown'
              )}
            </div>
          </div>
        )}

        {job && (
          <>
            <div className="qz-page-header align-items-start">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <JobStatusPill job={job} />
                  <LanguageCode language={job.language} />
                  <span className="qz-small qz-muted">
                    <Icon name="message" /> {t('job.field.created')}{' '}
                    <RelativeTime iso={job.createdAt} />
                  </span>
                </div>
                <h1 style={{ maxWidth: '44rem' }}>{job.title ?? truncate(job.request, 120)}</h1>
              </div>
              <div className="d-flex gap-2">
                {job.status !== 'planning' && (
                  <Link className="btn btn-primary" to={`/tasks?jobId=${jobId}`}>
                    {t('job.page.reviewDrafts')}
                  </Link>
                )}
                {(job.status === 'planning' || job.status === 'running') &&
                  (confirming ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="qz-small">{t('job.page.confirmCancel')}</span>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={cancelling}
                        onClick={handleCancel}
                      >
                        {t('job.page.cancelJob')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={cancelling}
                        onClick={() => setConfirming(false)}
                      >
                        {t('job.page.keepRunning')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => setConfirming(true)}
                    >
                      {t('job.page.cancelJob')}
                    </button>
                  ))}
              </div>
            </div>

            {cancelError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{t(cancelError === 'unknown' ? 'error.unknown' : `error.${cancelError}`)}</div>
              </div>
            )}

            {job.status === 'failed' && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div className="qz-mono qz-small">{job.error ?? t('job.status.failed')}</div>
              </div>
            )}

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="qz-card">
                  <div className="qz-card-body">
                    {job.chunks.length === 0 ? (
                      <span className="fw-semibold">{t('job.page.planning')}</span>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                          <span className="fw-semibold">
                            {t('job.page.progress', {
                              done: job.chunks.filter((chunk) => chunk.status === 'done').length,
                              total: job.chunks.length,
                            })}
                          </span>
                        </div>
                        <div
                          className="progress"
                          role="progressbar"
                          aria-valuenow={finishedPercent}
                        >
                          <div
                            className={progressBarClass(job.status)}
                            style={{ width: `${finishedPercent}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {job.chunks.length > 0 && <JobChunkTable chunks={job.chunks} />}
                </div>

                {!isPlanning && (
                  <div className="qz-card">
                    <div className="qz-card-header">
                      <h3>{t('job.page.latestDrafts')}</h3>
                      <Link className="qz-small" to={`/tasks?jobId=${jobId}`}>
                        {t('job.page.allDrafts')}
                      </Link>
                    </div>
                    {drafts.length === 0 ? (
                      <div className="qz-empty">{t('job.page.noDrafts')}</div>
                    ) : (
                      <table className="table table-hover table-clickable align-middle">
                        <tbody>
                          {drafts.map((draftTask) => {
                            const text =
                              draftTask.translations[draftTask.originalLanguage]?.text ?? '';
                            return (
                              <tr
                                key={draftTask.taskId}
                                role="button"
                                tabIndex={0}
                                onClick={() => openTask(draftTask.taskId)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    openTask(draftTask.taskId);
                                  } else if (e.key === ' ') {
                                    e.preventDefault();
                                    openTask(draftTask.taskId);
                                  }
                                }}
                              >
                                <td className="fw-semibold">{truncate(text, 80)}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    {draftTask.taskTypes.map((taskType) => (
                                      <TaskTypeBadge key={taskType} type={taskType} />
                                    ))}
                                  </div>
                                </td>
                                <td className="text-end">
                                  <DifficultyMeter difficulty={draftTask.difficulty} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>

              <div className="col-lg-4">
                <JobCostCard job={job} />

                <div className="qz-card">
                  <div className="qz-card-header">
                    <h3>{t('job.page.request')}</h3>
                  </div>
                  <div className="qz-card-body qz-small">
                    <p style={{ whiteSpace: 'pre-wrap' }}>{job.request}</p>
                    <dl className="qz-kv">
                      <dt>{t('job.field.language')}</dt>
                      <dd>{t(`task.language.${job.language}`)}</dd>
                      <dt>{t('job.field.taskCount')}</dt>
                      <dd>{job.taskCount}</dd>
                      <dt>{t('job.field.sourceSites')}</dt>
                      <dd>{job.sourceSites.join(', ')}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default JobPage;
